import type { ComponentProps, ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import {
  attachComponentData,
  useElementFilter,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import {
  Content,
  Link,
  Page,
  Progress,
  RoutedTabs,
  WarningPanel,
} from '@backstage/core-components';
import {
  entityRouteRef,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';

import {
  docsPageWidth,
  getSurfaceBorder,
  getThemeTokens,
  pageContentWidth,
} from '../../../theme/tokens';
import { EntityHero } from './EntityHero';

const dataKey = 'app.catalog.entityPageShellRoute';

type EntityPageShellRouteProps = {
  children: JSX.Element;
  if?: (entity: Entity) => boolean;
  path: string;
  tabProps?: ComponentProps<typeof RoutedTabs>['routes'][number]['tabProps'];
  title: string;
};

type EntityPageShellComponent = ((props: {
  children: ReactNode;
  NotFoundComponent?: ReactNode;
}) => JSX.Element) & {
  Route: (props: EntityPageShellRouteProps) => null;
};

const Route = (_props: EntityPageShellRouteProps) => null;

attachComponentData(Route, dataKey, true);
attachComponentData(Route, 'core.gatherMountPoints', true);

export const EntityPageShell: EntityPageShellComponent = ({
  children,
  NotFoundComponent,
}: {
  children: ReactNode;
  NotFoundComponent?: ReactNode;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { entity, error, loading } = useAsyncEntity();
  const { kind } = useRouteRefParams(entityRouteRef);

  const routes = useElementFilter(
    children,
    elements =>
      elements
        .selectByComponentData({
          key: dataKey,
          withStrictError:
            'Child of EntityPageShell must be an EntityPageShell.Route',
        })
        .getElements()
        .flatMap(({ props }) => {
          if (!entity) {
            return [];
          }

          const routeProps = props as EntityPageShellRouteProps;

          if (routeProps.if && !routeProps.if(entity)) {
            return [];
          }

          return [
            {
              children: routeProps.children,
              path: routeProps.path,
              tabProps: routeProps.tabProps,
              title: routeProps.title,
            },
          ];
        }),
    [entity],
  );

  return (
    <Page themeId="home">
      <Box
        sx={{
          boxSizing: 'border-box',
          gridColumn: '1 / -1',
          px: { md: 3, xs: 2 },
          py: { md: 2.25, xs: 2 },
          width: '100%',
          // Widen hero and tabs together on the Docs tab so they stay aligned.
          '&:has(.techdocs-reader-page) > *': { maxWidth: docsPageWidth },
        }}
      >
        <Box
          sx={{
            margin: '0 auto',
            maxWidth: pageContentWidth,
            width: '100%',
          }}
        >
          {entity ? <EntityHero /> : null}
        </Box>

        {loading ? <Progress /> : null}

        {entity ? (
          <Box
            sx={{
              margin: '0 auto',
              maxWidth: pageContentWidth,
              '&:has(.swagger-ui)': { maxWidth: 'none' },
              pt: 0.5,
              width: '100%',
              '& .BackstageHeaderTabs-tabsWrapper': {
                backgroundColor: 'transparent',
                borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                paddingLeft: 0,
              },
              '& .BackstageHeaderTabs-defaultTab': {
                color: tokens.textSecondary,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: 0,
                minHeight: 48,
                padding: theme.spacing(1.5, 2),
                textTransform: 'none',
              },
              '& .BackstageHeaderTabs-tabRoot:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
              '& .MuiTabs-indicator': {
                borderRadius: '999px 999px 0 0',
                height: 3,
              },
              '& .BackstageContent-root': {
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: theme.spacing(3),
              },
            }}
          >
            <RoutedTabs routes={routes} />
          </Box>
        ) : null}

        {error ? (
          <Box
            sx={{
              margin: '0 auto',
              maxWidth: pageContentWidth,
              pt: 3,
              width: '100%',
            }}
          >
            <Content noPadding>
              <Alert severity="error">{error.toString()}</Alert>
            </Content>
          </Box>
        ) : null}

        {!loading && !error && !entity ? (
          <Box
            sx={{
              margin: '0 auto',
              maxWidth: pageContentWidth,
              pt: 3,
              width: '100%',
            }}
          >
            <Content noPadding>
              {NotFoundComponent ?? (
                <WarningPanel title="Entity not found">
                  There is no {kind} with the requested{' '}
                  <Link to="https://backstage.io/docs/features/software-catalog/references">
                    kind, namespace, and name
                  </Link>
                  .
                </WarningPanel>
              )}
            </Content>
          </Box>
        ) : null}
      </Box>
    </Page>
  );
};

EntityPageShell.Route = Route;
